import React, { useCallback, useEffect, useState } from 'react';

import { useIntl } from 'react-intl';

import { useStoreContext } from 'state/store';
import { isOpenIfNotWithinMinutesOfCloseTodayAndNot24hours } from 'utils/restaurant';

import { MINUTE_IN_MS, OPTION_NOW } from './constants';
import { options } from './options.th';
import { Container, Fieldset, Group, Label, Legend, Radio } from './styled.th';
import { IOptionSettings, IPickupTimeSelectorProps } from './types';
import { deriveDefaultOptionFromValue, getNearestTimeSlot } from './utils';

const PickupTimeSelector = ({
  onChange,
  showAsTime,
  setStoreIsClosing,
  value,
}: IPickupTimeSelectorProps) => {
  const [selectedOption, setSelectedOption] = useState<IOptionSettings | null>(() =>
    deriveDefaultOptionFromValue(value, options)
  );
  const { formatMessage, formatTimeToParts } = useIntl();
  const { store } = useStoreContext();
  const nearestTimeSlot = getNearestTimeSlot();

  const getTimeString = (minutes: number) => {
    const currentTime = new Date().getTime();
    const [hour, literal, minute] = formatTimeToParts(
      new Date(currentTime + minutes * MINUTE_IN_MS)
    );
    return `${hour.value}${literal.value}${minute.value}`;
  };
  const handleChange = useCallback(
    (option: IOptionSettings) => {
      const fireOrderIn = option.inSeconds;
      onChange(fireOrderIn);
      setSelectedOption(option);
    },
    [onChange]
  );

  const getOptionDateObject = useCallback(
    (minutes: number) => new Date(nearestTimeSlot + minutes * MINUTE_IN_MS),
    [nearestTimeSlot]
  );

  const validPickupTime = useCallback(
    (minutes: number) => {
      const { diningRoomHours } = store;

      if (!diningRoomHours) {
        return false;
      }

      return isOpenIfNotWithinMinutesOfCloseTodayAndNot24hours(
        diningRoomHours,
        getOptionDateObject(minutes),
        30
      );
    },
    [getOptionDateObject, store]
  );

  const isNowAValidPickupTime = validPickupTime(OPTION_NOW.inMinutes);
  const validOptions = options.filter(pickupOption => validPickupTime(pickupOption.inMinutes));

  useEffect(() => {
    if (setStoreIsClosing && !isNowAValidPickupTime) {
      setStoreIsClosing(true);
    }
  }, [setStoreIsClosing, isNowAValidPickupTime]);

  if (!isNowAValidPickupTime) {
    return (
      <Container>
        <Legend>{formatMessage({ id: 'pickUpOrderRestaurantClosing' })}</Legend>
      </Container>
    );
  }

  return (
    <Fieldset data-testid="pickup-time-selector">
      <Legend>{formatMessage({ id: showAsTime ? 'pickUpOrderAt' : 'pickUpOrderIn' })}</Legend>
      <Container>
        {validOptions.map(pickupOption => (
          <Group key={pickupOption.id}>
            <Radio
              id={pickupOption.id}
              data-testid={pickupOption.id}
              name="pickUpTime"
              type="radio"
              aria-label={formatMessage(pickupOption.ariaLabel)}
              value={pickupOption.inSeconds}
              checked={selectedOption?.id === pickupOption.id}
              onChange={() => handleChange(pickupOption)}
            />
            <Label htmlFor={pickupOption.id} isSelected={selectedOption?.id === pickupOption.id}>
              {showAsTime && pickupOption.id !== 'now'
                ? getTimeString(pickupOption.inMinutes)
                : formatMessage({ id: pickupOption.labelId })}
            </Label>
          </Group>
        ))}
      </Container>
    </Fieldset>
  );
};

export default PickupTimeSelector;
